import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { TextField, Button, CircularProgress, Container, Box, Typography, MenuItem, Snackbar, Alert, FormControl, InputLabel, Select } from '@mui/material';
import { useCompleteRegistrationMutation, useGenerateFormLinkMutation, useFetchOrganizationsQuery } from '../../../services/profileService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

const CompleteRegistration = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { register, handleSubmit, formState: { errors } } = useForm();
  const [completeRegistration, { isLoading }] = useCompleteRegistrationMutation();
  const [generateFormLink] = useGenerateFormLinkMutation();
  const { data: organizations, isLoading: orgLoading } = useFetchOrganizationsQuery(); // Fetch organizations
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserInfo(decoded);
        const orgFromToken =
          decoded?.organization_id ??
          decoded?.org_id ??
          decoded?.organizationId;
        if (orgFromToken != null && String(orgFromToken) !== "") {
          setSelectedOrgId(String(orgFromToken));
        }
      } catch (error) {
        console.error("Invalid token:", error);
        showToast("Invalid or expired token.", "error");
        navigate('/login'); // Redirect to login if token is invalid
      }
    }
  }, [token, navigate]);

  const handleToastClose = () => {
    setToastOpen(false);
  };

  const showToast = (message, severity) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleOrgChange = (event) => {
    setSelectedOrgId(event.target.value);
  };

  const orgIdFromToken =
    userInfo?.organization_id ?? userInfo?.org_id ?? userInfo?.organizationId;
  const isOrgFromToken =
    orgIdFromToken != null && String(orgIdFromToken) !== "";

  const onSubmit = async (data) => {
    if (!selectedOrgId) {
      showToast("Please select an organization.", "error");
      return;
    }

    try {
      const requestData = {
        ...data,
        token: token,
        organization_id: selectedOrgId,
      };

      await completeRegistration(requestData).unwrap();

      showToast("Registration completed successfully! Now Generating a role review URL", "success");

      const formLinkResponse = await generateFormLink({
        email: userInfo?.email,
        organization_id: selectedOrgId,
      }).unwrap();
      console.log(formLinkResponse)

      if (formLinkResponse?.data?.registration_url) {
        showToast("URL generated and redirecting!", "success");
        setTimeout(() => {
          window.location.href = formLinkResponse?.data?.registration_url;
        }, 2000); // Redirect after 2 seconds
      } else {
        showToast("Form Link could not be generated. Please contact support.", "error");
      }

    } catch (error) {
      console.error("Error completing registration:", error);
      showToast("Failed to complete registration.", "error");
    } 
  };

  if (!token) {
    return <Typography>Invalid registration link. Please contact support.</Typography>;
  }

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 4 }}>Complete Your Registration</Typography>
      <Box sx={{ mb: 4 }}>
        <form onSubmit={handleSubmit(onSubmit)}>

          {/* Organization Dropdown */}
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="organization-select-label">Organization</InputLabel>
            <Select
              labelId="organization-select-label"
              id="organization-select"
              value={selectedOrgId}
              label="Organization"
              onChange={handleOrgChange}
              disabled={orgLoading || isOrgFromToken}
              required
            >
              {organizations ? organizations.data.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              )) : null}
            </Select>
          </FormControl>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Password"
              variant="outlined"
              type="password"
              {...register('password', { required: 'Password is required' })}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
          </Box>


          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isLoading || orgLoading || !selectedOrgId}  // Disable during load/no org selected
            sx={{ mb: 3 }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Continue to Role Review'}
          </Button>

        </form>
      </Box>

      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleToastClose} severity={toastSeverity} variant="filled">
          {toastMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CompleteRegistration;